import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAdminUsers } from '../../hooks/useAdmin';
import type { AdminUser } from '../../services/api/admin';

/**
 * Searchable user picker (combobox with chips), following the app's standard
 * combobox pattern (see external-users/ProjectPicker). Sources options from
 * `useAdminUsers()` — admin-gated, same as every surface that embeds this.
 *
 * `single` limits the selection to one user: picking a user replaces the
 * current selection instead of appending. Value stays `number[]` in both
 * modes so callers store user ids uniformly.
 */

export function userDisplayName(u: Pick<AdminUser, 'firstName' | 'lastName' | 'email'>): string {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
  return name || u.email;
}

export interface UserPickerProps {
  value: number[];
  onChange: (next: number[]) => void;
  single?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export function UserPicker({ value, onChange, single, placeholder, disabled }: UserPickerProps) {
  const { data: users, isLoading, isError } = useAdminUsers();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const allUsers = useMemo(() => users ?? [], [users]);

  const selected = useMemo(() => allUsers.filter((u) => value.includes(u.id)), [allUsers, value]);
  // Ids not present in the (non-deleted) admin user list — e.g. a since-removed
  // account. Rendered as an explicit stale chip so it stays removable.
  const staleIds = useMemo(
    () => value.filter((id) => !allUsers.some((u) => u.id === id)),
    [allUsers, value],
  );

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allUsers
      .filter((u) => !value.includes(u.id))
      .filter((u) =>
        q ? userDisplayName(u).toLowerCase().includes(q) || u.email.toLowerCase().includes(q) : true,
      );
  }, [allUsers, value, query]);

  const add = (id: number) => {
    onChange(single ? [id] : value.includes(id) ? value : [...value, id]);
    setQuery('');
    if (single) {
      setIsOpen(false);
    } else {
      inputRef.current?.focus();
    }
  };

  const remove = (id: number) => {
    onChange(value.filter((v) => v !== id));
  };

  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading users…</p>;
  }
  if (isError) {
    return <p className="text-sm text-red-600">Failed to load users.</p>;
  }

  const chip = (key: number, label: string, stale = false) => (
    <li
      key={key}
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium border ${
        stale
          ? 'bg-amber-50 text-amber-700 border-amber-200'
          : 'bg-indigo-50 text-indigo-700 border-indigo-200'
      }`}
    >
      <span>{label}</span>
      <button
        type="button"
        onClick={() => remove(key)}
        disabled={disabled}
        aria-label={`Remove ${label}`}
        className="text-current opacity-70 hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 rounded"
      >
        <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </li>
  );

  return (
    <div ref={containerRef} role="group" aria-label="User selection">
      {(selected.length > 0 || staleIds.length > 0) && (
        <ul className="mb-1 flex flex-wrap gap-1.5">
          {selected.map((u) => chip(u.id, userDisplayName(u)))}
          {staleIds.map((id) => chip(id, `Former user #${id}`, true))}
        </ul>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder ?? 'Search users…'}
          disabled={disabled}
          autoComplete="off"
          className="w-full rounded-lg border border-gray-300 py-2 pl-3 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        <button
          type="button"
          onClick={() => {
            setIsOpen((open) => !open);
            inputRef.current?.focus();
          }}
          disabled={disabled}
          aria-label={isOpen ? 'Close user list' : 'Open user list'}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
        >
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>

        {isOpen && (
          <div
            role="listbox"
            className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg"
          >
            {suggestions.length === 0 ? (
              <p className="px-3 py-2 text-sm text-gray-500">No matching users.</p>
            ) : (
              suggestions.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  role="option"
                  aria-selected="false"
                  onClick={() => add(u.id)}
                  disabled={disabled}
                  className="flex w-full items-baseline justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                >
                  <span className="text-gray-900">{userDisplayName(u)}</span>
                  <span className="truncate text-xs text-gray-400">{u.email}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
